import React, { useState, useEffect, useCallback, useRef } from "react";
import { z } from "zod";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useUserContext } from "../config/UserContext";
import { useKeycloak } from "../config/KeycloakContext";
import { useLoadScript } from "@react-google-maps/api";
import type { Freeze } from "../../../shared/types/models/freeze";

const libraries: any = ['places'];

function useNewPlacesAutocomplete(selectedCountry: string) {
    const [value, setValue] = useState('');
    const [suggestions, setSuggestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const sessionTokenRef = useRef<any>(null);

    const initToken = () => {
        if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
            sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
        }
    };

    const fetchSuggestions = useCallback(async (input: string) => {
        setValue(input);
        if (!input) {
            setSuggestions([]);
            return;
        }

        if (!window.google?.maps?.places?.AutocompleteSuggestion) {
            return;
        }

        initToken();
        setLoading(true);

        try {
            const request: any = {
                input,
                sessionToken: sessionTokenRef.current,
            };
            
            if (selectedCountry) {
                request.includedRegionCodes = [selectedCountry.toLowerCase()];
            }

            const response = await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
            setSuggestions(response.suggestions || []);
        } catch (error) {
            setSuggestions([]);
        } finally {
            setLoading(false);
        }
    }, [selectedCountry]);

    const clearSuggestions = useCallback(() => {
        setSuggestions([]);
    }, []);

    return { value, setValue, suggestions, fetchSuggestions, clearSuggestions, loading };
}
import type { Parcel } from "../../../shared/types/models/parcel";

interface SimpleInstrumentCardProps {
  property: Freeze;
  setProperty: (p: Freeze) => void;
  onContinue: () => void;
}

const SimpleInstrumentCard: React.FC<SimpleInstrumentCardProps> = ({ property, setProperty, onContinue }) => {
  const { county, state, apiBaseUrl, parcels, setParcels } = useUserContext();
  const { getToken } = useKeycloak();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const initialParcel = parcels && parcels.length > 0 ? parcels[0] : {};
  const [indexesData, setIndexesData] = useState<Record<string, string>>(property?.recordingIndexes || {});
  const [parcelData, setParcelData] = useState<Partial<Parcel>>(initialParcel);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const { isLoaded } = useLoadScript({
      googleMapsApiKey: import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '',
      libraries,
      version: "weekly"
  });

  const {
      value: addressValue,
      setValue: setAddressValue,
      suggestions: addressSuggestions,
      fetchSuggestions: fetchAddressSuggestions,
      clearSuggestions: clearAddressSuggestions,
  } = useNewPlacesAutocomplete('US');

  useEffect(() => {
    if (initialParcel?.street1 && !addressValue) {
      setAddressValue(initialParcel.street1);
    }
  }, [initialParcel?.street1]);

  useEffect(() => {
    if (addressValue !== parcelData.street1) {
       setParcelData(prev => ({ ...prev, street1: addressValue }));
    }
  }, [addressValue]);

  const handleAddressSelect = async (suggestion: any) => {
      const formatted = (suggestion.placePrediction?.text?.text || '').replace(/,\s*USA$/, '').replace(/\s*USA$/, '').trim();
      setAddressValue(formatted);
      setParcelData(prev => ({ ...prev, street1: formatted, address: formatted }));
      clearAddressSuggestions();
  };

  const schemaSource = county?.documentSchema || county?.simpleDocumentSchema;
  const parsedSchema = typeof schemaSource === 'string'
    ? (() => { try { return JSON.parse(schemaSource); } catch { return null; } })()
    : schemaSource;

  const indexes = parsedSchema?.indexes || [];
  const parcelSchema = parsedSchema?.parcelSchema || {
    requiresAddress: true,
    requiresLegalDescription: true,
    parcelIndexes: []
  };

  const getDynamicSchema = () => {
    let schemaObj: Record<string, any> = {};
    
    // Top-level Freeze Indexes
    indexes.forEach((idx: any) => {
      const isReq = idx.required !== false && String(idx.required).toLowerCase() !== "false" && idx.required !== 0;
      if (isReq) {
        schemaObj[idx.key] = z.string().min(1, `${idx.label} is required`);
      } else {
        schemaObj[idx.key] = z.string().optional();
      }
    });

    // Parcel Info
    if (parcelSchema.requiresAddress) {
      schemaObj.street1 = z.string().min(1, "Address is required");
    }
    if (parcelSchema.requiresLegalDescription) {
      schemaObj.legalDescription = z.string().min(1, "Legal Description is required");
    }
    if (parcelSchema.parcelIndexes) {
      parcelSchema.parcelIndexes.forEach((idx: any) => {
        const isReq = idx.required !== false && String(idx.required).toLowerCase() !== "false" && idx.required !== 0;
        if (isReq) {
          schemaObj[`index_${idx.key}`] = z.string().min(1, `${idx.label} is required`);
        } else {
          schemaObj[`index_${idx.key}`] = z.string().optional();
        }
      });
    }
    return z.object(schemaObj);
  };

  const schema = getDynamicSchema();

  const getFormData = () => {
    const data: Record<string, any> = {
      ...indexesData,
      street1: parcelData.street1,
      legalDescription: parcelData.legalDescription
    };
    if (parcelSchema.parcelIndexes) {
      parcelSchema.parcelIndexes.forEach((idx: any) => {
        data[`index_${idx.key}`] = parcelData.recordingIndexes?.[idx.key];
      });
    }
    return data;
  };

  const formData = getFormData();
  const isFormValid = schema.safeParse(formData).success;

  const handleBlur = (field: string, value: any) => {
    setTouched(t => ({ ...t, [field]: true }));
    const fieldSchema = schema.shape[field];
    if (fieldSchema) {
      const result = fieldSchema.safeParse(value);
      if (!result.success) {
        setErrors(e => ({ ...e, [field]: result.error.errors[0].message }));
      } else {
        setErrors(e => {
          const newE = { ...e };
          delete newE[field];
          return newE;
        });
      }
    }
  };

  const handleChange = (field: string, value: string, isParcel: boolean = false) => {
    if (isParcel) {
      if (field.startsWith('index_')) {
        const key = field.replace('index_', '');
        setParcelData(prev => ({
          ...prev,
          recordingIndexes: {
            ...(prev.recordingIndexes || {}),
            [key]: value
          }
        }));
      } else {
        setParcelData(prev => ({ ...prev, [field]: value }));
      }
    } else {
      setIndexesData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const currentFormData = getFormData();
    
    const newTouched: Record<string, boolean> = {};
    Object.keys(currentFormData).forEach(k => newTouched[k] = true);
    setTouched(newTouched);

    const result = schema.safeParse(currentFormData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      const token = await getToken();
      if (!token) throw new Error("Not authenticated");

      const headers = {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`,
      };

      // 1. Save top-level Freeze indexes
      const freezeRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${property?.lookup}/recording-indexes`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ recordingIndexes: indexesData }),
      });
      if (!freezeRes.ok) throw new Error("Failed to save county indexes");
      
      const updatedFreezeRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${property?.lookup}`, { headers });
      const updatedFreeze = await updatedFreezeRes.json();
      if (updatedFreeze?.data) setProperty(updatedFreeze.data);

      // 2. Save Parcel
      const searchParts = [county?.id, property?.lookup];
      if (parcelSchema.parcelIndexes) {
        parcelSchema.parcelIndexes.forEach((idx: any) => {
          if (parcelData.recordingIndexes?.[idx.key]) {
            searchParts.push(parcelData.recordingIndexes[idx.key]);
          }
        });
      }
      const searchString = searchParts.join('-');

      const parcelPayload = {
        ...parcelData,
        search: searchString,
        stateId: state?.id,
        countyId: county?.id,
      };

      const parcelRes = await fetch(`${apiBaseUrl}/api/frozendeed/freeze/${property?.lookup}/parcels`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ parcels: [parcelPayload] }),
      });
      if (!parcelRes.ok) throw new Error("Failed to save property info");
      
      const pData = await parcelRes.json();
      setParcels(pData.data);
      
      setSaving(false);
      onContinue();
    } catch (err: unknown) {
      if (err instanceof Error) {
        setSaveError(err.message || "Failed to save details");
      } else {
        setSaveError("Failed to save details");
      }
      setSaving(false);
    }
  };

  return (
    <form className="w-full flex flex-col gap-4" onSubmit={handleSubmit} autoComplete="off">
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-2">
        <p className="text-sm text-blue-700">
          Enter the details below to protect your property.
        </p>
      </div>

      {indexes.length > 0 && (
        <>
          <h3 className="text-lg font-bold text-[#141435] border-b border-[#D7DFEC] pb-2 mt-2">County Information</h3>
          {indexes.map((idx: any) => (
            <div key={idx.key} className="flex flex-col gap-1 w-full">
              <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta flex items-center gap-1 relative">
                {idx.label} {idx.required !== false && String(idx.required).toLowerCase() !== "false" && idx.required !== 0 && <span className="text-red-500">*</span>}
                {idx.description && (
                  <span className="relative group cursor-pointer ml-1">
                    <InformationCircleIcon className="w-4 h-4 text-[#0B4BB6]" />
                    <span className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-white border border-[#D7DFEC] rounded shadow-lg px-3 py-2 text-xs text-[#485D7D] w-64 font-normal font-plus-jakarta">
                      {idx.description}
                    </span>
                  </span>
                )}
              </label>
              <input
                type="text"
                value={indexesData[idx.key] || ''}
                onChange={e => handleChange(idx.key, e.target.value, false)}
                onBlur={e => handleBlur(idx.key, e.target.value)}
                className={`w-full px-3 py-2 rounded border ${errors[idx.key] && touched[idx.key] ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none`}
                placeholder={idx.label}
              />
              {errors[idx.key] && touched[idx.key] && <span className="text-red-500 text-xs mt-0.5">{errors[idx.key]}</span>}
            </div>
          ))}
        </>
      )}

      <h3 className="text-lg font-bold text-[#141435] border-b border-[#D7DFEC] pb-2 mt-4">Property Information</h3>
      
      {parcelSchema.parcelIndexes && parcelSchema.parcelIndexes.map((idx: any) => (
        <div key={idx.key} className="flex flex-col gap-1 w-full">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">
            {idx.label} {idx.required !== false && <span className="text-red-500">*</span>}
          </label>
          <input
            type="text"
            className={`w-full px-3 py-2 rounded border ${errors[`index_${idx.key}`] && touched[`index_${idx.key}`] ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none`}
            value={parcelData.recordingIndexes?.[idx.key] || ''}
            onChange={e => handleChange(`index_${idx.key}`, e.target.value, true)}
            onBlur={e => handleBlur(`index_${idx.key}`, e.target.value)}
          />
          {errors[`index_${idx.key}`] && touched[`index_${idx.key}`] && <span className="text-red-500 text-xs">{errors[`index_${idx.key}`]}</span>}
        </div>
      ))}

      {parcelSchema.requiresAddress && (
        <div className="flex flex-col gap-1 w-full">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta flex items-center gap-1 relative">
            Property Address <span className="text-red-500">*</span>
            <span className="relative group cursor-pointer ml-1">
              <InformationCircleIcon className="w-4 h-4 text-[#0B4BB6]" />
              <span className="absolute left-6 top-1/2 -translate-y-1/2 z-10 hidden group-hover:block bg-white border border-[#D7DFEC] rounded shadow-lg px-3 py-2 text-xs text-[#485D7D] w-64 font-normal font-plus-jakarta">
                Enter the full address of the property (e.g., 123 Main St, City, ST 12345)
              </span>
            </span>
          </label>
          <div className="relative">
            <input
              type="text"
              className={`w-full px-3 py-2 rounded border ${errors.street1 && touched.street1 ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none`}
              value={addressValue || ''}
              onChange={e => fetchAddressSuggestions(e.target.value)}
              onBlur={e => handleBlur('street1', e.target.value)}
              placeholder="Search or enter full address"
            />
            {addressSuggestions.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border border-[#D7DFEC] rounded shadow-lg mt-1 max-h-60 overflow-auto">
                {addressSuggestions.map((suggestion: any, index: number) => (
                  <li
                    key={index}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-50 text-[14px] text-[#141435]"
                    onMouseDown={e => {
                      e.preventDefault();
                      handleAddressSelect(suggestion);
                    }}
                  >
                    {suggestion.placePrediction.text.text}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {errors.street1 && touched.street1 && <span className="text-red-500 text-xs">{errors.street1}</span>}
        </div>
      )}

      {parcelSchema.requiresLegalDescription && (
        <div className="flex flex-col gap-1 w-full mt-2">
          <label className="text-[#485D7D] text-[13px] font-medium font-plus-jakarta">
            Legal Description <span className="text-red-500">*</span>
          </label>
          <textarea
            className={`w-full px-3 py-2 rounded border ${errors.legalDescription && touched.legalDescription ? 'border-red-500' : 'border-[#D7DFEC]'} bg-white text-[#141435] text-[15px] font-plus-jakarta focus:outline-none min-h-[120px]`}
            value={parcelData.legalDescription || ''}
            onChange={e => handleChange('legalDescription', e.target.value, true)}
            onBlur={e => handleBlur('legalDescription', e.target.value)}
            placeholder="Enter the full legal description from your deed"
          />
          {errors.legalDescription && touched.legalDescription && <span className="text-red-500 text-xs">{errors.legalDescription}</span>}
        </div>
      )}

      <button
        type="submit"
        className={`w-full bg-[#0B4BB6] text-white font-bold text-[15px] font-plus-jakarta py-2.5 rounded-full mt-6 hover:bg-[#23235a] transition-colors border-2 border-[#0B4BB6] ${(!isFormValid || saving) ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={!isFormValid || saving}
      >
        {saving ? 'Saving...' : 'Save & Continue'}
      </button>
      {saveError && <div className="text-red-500 mt-2 text-center font-semibold">{saveError}</div>}
    </form>
  );
};

export default SimpleInstrumentCard;
